import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { showError, showSuccess } from "redux/actions/ui";
import { IAPIResponse, StatusCode } from "api/protocols";
import { UserActive, UserLogout } from "api/rpc";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { convertTime, formatDate, formatDateFromString } from "helpers/Helpers";
import PortalDatePickerInput from "elements/teesheet/PortalDatePickerInput";
import DateBar from "elements/teesheet/DateBar";
import "./clientPortalHome.scss";
import moment from "moment";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { ClientPortalNavigation } from "../ClientPortalNavigation";
import CustomDropdown from "components/customDropdown/CustomDropdown";
import CustomDropdownOption from "components/customDropdown/CustomDropdownOption";
import ReactDOM from "react-dom";
import { GetTeeTimePromotionDates, GetTeeTimesAccessCode, GetTeeTimesCustomer } from "api/rpc/2022-09/customer/teetime";
import { GetGuestTeeTimePromotionDates, GetTeeTimesGuest } from "api/rpc/2022-09/guest/teetime";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { sortFacilities } from "redux/reducers/clientPortal";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { getPortalDateBarShowNavigation } from "helpers/portalDateControls";
import {
  DeleteFacilityFavourite,
  GetCustomerTeeSheetSettings,
  GetFacility,
  PostFacilityFavourite,
  TWebTeeTimes,
} from "api/rpc/2024-04/customer/facility";
import { GetFacilityGuest, GetGuestTeeSheetSettings } from "api/rpc/2024-04/guest/facility";
import Spin from "components/spin/spin";
import { GetClient } from "api/rpc/2024-04/customer/client";
import { GetClientGuest } from "api/rpc/2024-04/guest/client";
import { LockTeeTime } from "api/rpc/2024-04/customer/teetime";
import TeeTimeWaitlist from "components/teeTimeWaitlist/TeeTimeWaitlist";
import { useClientPortal } from "../useClientPortal";
import classNames from "classnames";
import Popup from "components/popup/Popup";

import ClientPortalTeetimeFilters from "./ClientPortalTeetimeFilters";
import LoaderCard from "components/loaderCard/LoaderCard";
import ClientPortalTeeTimesList from "./ClientPortalTeeTimesList/ClientPortalTeeTimesList";
import ClientPortalTeeSheet from "./ClientPortalTeeSheet/ClientPortalTeeSheet";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { ISlot, ITeeTime } from "redux/reducers/models/teetime";
import { IFacility } from "redux/reducers/models/facility";
import axios, { CancelToken } from "axios";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import { GetCustomerNotifications, INotification } from "api/rpc/2024-04/customer/teesheet";
import { GetGuestNotifications } from "api/rpc/2024-04/guest/teesheet";
import { GetCustomerLeague } from "api/rpc/2024-04/customer/league";
import { ILeague } from "redux/reducers/models/league";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import Toggle from "components/form/toggle/Toggle";
import { UserType } from "redux/reducers/models/user";
import { GetRWGToken, PostRWGToken } from "api/rpc/2024-04/customer/customer";

export interface IFilterState {
  selectedDate: Date;
  selectedPlayers: string;
  selectedHoles: string;
  selectedCart: string;
  selectedPromotion: string;
  selectedCourseId: number;
  enable_cart_bookings: boolean;
  facilitySearchQuery: string;
  adminUserLoggedIn: boolean;
  teeTimeRes: IAPIResponse<Array<ITeeTime>>;
  notifications: Array<INotification>;
}

interface IParams {
  clientShortName: string;
  facilityShortName: string;
}

interface ILeagueState {
  leagues: Array<Partial<ILeague>>;
  selectedLeague: Partial<ILeague>;
}

export default function ClientPortalHome() {
  const history = useHistory();
  const { t } = useTranslation();
  const clientPortalStore = useAppSelector(store => store.clientPortalStore);
  const dispatch = useAppDispatch();
  const { clientShortName, facilityShortName } = useParams<IParams>();
  const { getURLParams, getDisabledBookings } = useClientPortal();
  const { date, waitlist_reference_id, rwg_token } = getURLParams();
  const { cancelToken, cancelRequest } = useCancelToken();

  const [teeTimeTab, setTeeTimeTab] = useState<TWebTeeTimes>(
    !!clientPortalStore?.user && clientPortalStore?.teeSheetSettings?.web_tee_times_display !== "list"
      ? clientPortalStore?.teeSheetSettings?.web_tee_times_display
      : "list",
  );

  const windowSize = useWindowSize();
  //Backend counts Sunday as day 7
  const [filterDayState, setFilterDayState] = useState<number>(initializeFilterDay());
  const [animateFavourites, setAnimateFavourites] = useState<boolean>(false);

  const [leagueState, setLeagueState] = useState<ILeagueState>({
    /**On default, -1 as the league id is selected, which means all tee times will be loaded */
    leagues: [{ id: -1, name: t("All Tee Times") }],
    selectedLeague: { id: -1, name: t("All Tee Times") },
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    selectedDate: date ? new Date(date.concat(`T${moment().format("hh:mm")}`)) : new Date(),
    selectedPlayers: null,
    selectedHoles: null,
    selectedCart: null,
    selectedPromotion: null,
    selectedCourseId: -1,
    enable_cart_bookings: null,
    facilitySearchQuery: "",
    adminUserLoggedIn: false,
    teeTimeRes: null,
    notifications: [],
  });

  const [showRestrictedBookings, setShowRestrictedBookings] = useState<boolean>(true);
  const [promotionDates, setPromotionDates] = useState<string[]>([]);
  const [visibleDateRange, setVisibleDateRange] = useState<{ start: string; end: string } | null>(null);

  useEffect(() => {
    const source = axios.CancelToken.source();
    window.scrollTo({ top: 0, left: 0 });
    void getClient(source.token);
    return () => source.cancel();
  }, []);

  useEffect(() => {
    if (clientPortalStore?.user) {
      // TODO: API call to set rwg_token
      void setRWGToken();
    }
  }, [rwg_token, clientPortalStore?.user]);

  useEffect(() => {
    const facilityId = clientPortalStore?.selectedFacility?.id;
    if (!facilityId || !visibleDateRange) {
      setPromotionDates([]);
      return;
    }

    const source = axios.CancelToken.source();
    void loadPromotionDates(
      facilityId,
      visibleDateRange.start,
      visibleDateRange.end,
      !!clientPortalStore?.user,
      source.token,
    );

    return () => source.cancel();
  }, [clientPortalStore?.selectedFacility?.id, clientPortalStore?.user, visibleDateRange?.start, visibleDateRange?.end]);

  async function setRWGToken() {
    if (rwg_token) {
      const res = await PostRWGToken(
        { rwg_token, facility_id: clientPortalStore?.selectedFacility?.id, customer_id: clientPortalStore?.user?.id },
        true,
      );
      if (res?.status !== StatusCode.OK) {
        return;
      }
      dispatch(
        clientPortalUpdate({
          currentRwgToken: res?.data,
        }),
      );
    } else {
      const res = await GetRWGToken(false);
      if (res?.status !== StatusCode.OK) {
        return;
      }
      dispatch(
        clientPortalUpdate({
          currentRwgToken: res?.data,
        }),
      );
    }
  }

  function sendToNextPage(address: string) {
    history.push(address);
  }

  const facilities = useMemo(() => {
    if (filterState?.facilitySearchQuery) {
      return clientPortalStore?.client?.facilities?.filter(
        facility =>
          facility?.long_name.toLowerCase().includes(filterState?.facilitySearchQuery?.toLowerCase()) ||
          facility?.city.toLowerCase().includes(filterState?.facilitySearchQuery?.toLowerCase()),
      );
    } else if (clientPortalStore?.client?.facilities) {
      return clientPortalStore?.client?.facilities;
    } else {
      return [];
    }
  }, [clientPortalStore?.client?.facilities, filterState.facilitySearchQuery]);

  function initializeFilterDay() {
    return moment(date ?? new Date()).day() === 0 ? 7 : moment(date ?? new Date()).day();
  }

  async function getClient(cancelToken: CancelToken) {
    let currentActiveUser = !!clientPortalStore?.user;
    let selectedClient = clientPortalStore?.client;
    let selectedFacility = clientPortalStore?.selectedFacility;

    const activeUserRes = await UserActive(false);
    if (activeUserRes?.status === StatusCode.OK) {
      currentActiveUser = true;
    }

    if (
      activeUserRes?.data?.user_type === UserType.FACILITY_ADMIN ||
      activeUserRes?.data?.user_type === UserType.CLIENT_ADMIN
    ) {
      setFilterState(prevState => ({ ...prevState, adminUserLoggedIn: true }));
      return;
    }

    if (
      !clientPortalStore?.client ||
      !clientPortalStore?.selectedFacility ||
      (clientPortalStore?.selectedFacility?.favourite === undefined && currentActiveUser)
    ) {
      let clientRes;
      let facilityRes;

      if (currentActiveUser) {
        currentActiveUser = true;
        clientRes = await GetClient({ short_name: clientShortName }, false);
      } else {
        clientRes = await GetClientGuest({ short_name: clientShortName }, false);
      }

      if (clientRes.status !== StatusCode.OK || clientRes?.data?.length === 0) {
        dispatch(showError(t("Error loading client")));
        return;
      }

      selectedClient = clientRes?.data[0];

      if (currentActiveUser) {
        facilityRes = await GetFacility({ client_id: selectedClient?.id, order_by: "long_name" }, false);
      } else {
        facilityRes = await GetFacilityGuest({ client_id: selectedClient?.id, order_by: "long_name" }, false);
      }

      if (facilityRes?.status !== StatusCode.OK || facilityRes?.data?.length === 0) {
        dispatch(showError(t("Error loading facilities")));
        return;
      }

      console.log(facilityRes?.data);

      //Sort by Favourite facilities
      const sortedFacilities = sortFacilities(facilityRes?.data);

      selectedFacility = selectedFacility
        ? sortedFacilities?.find(facility => facility?.id === selectedFacility?.id)
        : facilityShortName
        ? sortedFacilities?.find(facility => facility?.short_name === facilityShortName)
        : sortedFacilities[0];

      if (!selectedFacility && sortedFacilities?.length > 0) {
        selectedFacility = sortedFacilities[0];
      }

      selectedClient = { ...selectedClient, facilities: sortedFacilities };

      dispatch(
        clientPortalUpdate({
          client: selectedClient,
          selectedFacility,
          waitlist_reference_id: waitlist_reference_id ?? null,
          user: currentActiveUser ? activeUserRes?.data : undefined,
        }),
      );
    } else {
      dispatch(
        clientPortalUpdate({
          user: currentActiveUser ? activeUserRes?.data : undefined,
        }),
      );
    }

    void loadTeeTimes(
      selectedFacility,
      true,
      filterState.selectedDate,
      currentActiveUser,
      cancelToken,
      true,
      leagueState?.selectedLeague?.id,
      teeTimeTab,
      filterState?.selectedCourseId,
    );
  }

  function updateFilterState(newFilterState: Partial<IFilterState>) {
    setFilterState(cur => {
      return { ...cur, ...newFilterState };
    });
  }

  function handleSelectedDateChange(selectedDate: Date) {
    //Backend counts Sunday as day 7
    const dayOfWeekNumber = moment(selectedDate).day() === 0 ? 7 : moment(selectedDate).day();

    ReactDOM.unstable_batchedUpdates(() => {
      setFilterDayState(dayOfWeekNumber);
      updateFilterState({ selectedDate: selectedDate });
    });

    cancelRequest();

    if (clientPortalStore?.selectedFacility) {
      void loadTeeTimes(
        clientPortalStore?.selectedFacility,
        false,
        selectedDate,
        !!clientPortalStore?.user,
        cancelToken(),
        true,
        leagueState?.selectedLeague?.id,
        teeTimeTab,
        filterState?.selectedCourseId,
      );
    }
  }

  function getInitialBookingQuantity(teeTime: ITeeTime, slot: ISlot) {
    let initialQuantity = null;

    const disabledBookings = getDisabledBookings(
      clientPortalStore?.teeSheetSettings?.single_bookings,
      clientPortalStore?.teeSheetSettings?.twosome_bookings,
      clientPortalStore?.teeSheetSettings?.threesome_bookings,
      clientPortalStore?.teeSheetSettings?.foursome_bookings,
      teeTime?.quantity_remaining,
      teeTime?.size,
    );

    // If a type of booking is disabled ignore the slot that was clicked on
    if (
      disabledBookings.disableSingleBookings ||
      disabledBookings.disableTwosomeBookings ||
      disabledBookings.disableThreesomeBookings ||
      disabledBookings.disableFoursomeBookings
    ) {
      return disabledBookings.golferAmount;
    }

    if (teeTime?.slots && slot) {
      //If single bookings are not allowed and user selected position 1, auto select quantity to 2
      if (slot?.position === 1) {
        initialQuantity = 2;
      } else if (teeTime?.quantity_remaining === teeTime?.size) {
        //If single bookings are allowed within a group and the user selects position 1 in a fully open tee time, auto select
        //quantity to 2
        if (slot?.position === 1) {
          initialQuantity = 2;
          //Tee time slots are full open, auto select quantity as the slot position
        } else {
          initialQuantity = slot?.position;
        }
      } else {
        //Count the number of booked slots on the tee time.
        const bookingCount = teeTime?.slots?.reduce((prevCount, currentSlot) => {
          return currentSlot?.status === "booked" ? ++prevCount : prevCount;
        }, 0);
        //Set the quantity as the difference between the slot position and the # of booked slots
        initialQuantity = Math.abs(slot?.position - bookingCount);
      }
    }

    if (initialQuantity === 1) {
      initialQuantity = 2;
    }

    return initialQuantity;
  }

  async function checkTeeTime(date: string, teeTime: ITeeTime, slot?: ISlot) {
    const params = {
      date: date,
      id: teeTime?.id,
      facility_id: clientPortalStore?.selectedFacility?.id,
      turn_tee_time: true,
      extended: true,
      league_id: Number(leagueState.selectedLeague?.id) > 0 ? Number(leagueState.selectedLeague?.id) : undefined,
    };

    // If user is active, load auth route for tee time
    let teeInformationByIdRes;
    if (clientPortalStore?.user) {
      //Get tee time details by access code
      if (clientPortalStore?.usingAccessCode && clientPortalStore?.accessCode) {
        teeInformationByIdRes = await GetTeeTimesAccessCode(
          { ...params, access_code: clientPortalStore?.accessCode },
          true,
        );
      } else {
        //Regular tee time
        teeInformationByIdRes = await GetTeeTimesCustomer(params, true);
      }
    } else {
      teeInformationByIdRes = await GetTeeTimesGuest(params, true);
    }

    if (teeInformationByIdRes.status === StatusCode.GONE) {
      dispatch(showError(t("Tee time is currently being booked by another player")));
      return;
    }

    if (teeInformationByIdRes.status === StatusCode.LENGTH_REQUIRED) {
      dispatch(showError(t("Cannot book a tee time in the past")));
      return;
    }

    if (teeInformationByIdRes.status === StatusCode.PRECONDITION_FAILED) {
      dispatch(showError(teeInformationByIdRes.message));
      return;
    }

    if (teeInformationByIdRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error retrieving tee time")));
      return;
    }

    if (teeInformationByIdRes.data[0].quantity_remaining === 0) {
      dispatch(showError(t("Tee time has no spots available, please choose another")));
      return;
    }

    const tempId = String(teeTime?.id);
    const tempDate = String(date);
    const tempStartTime = convertTime(teeInformationByIdRes.data[0].start_time);
    const tempFormatDate = formatDateFromString(tempDate);

    const selectedTeeTime = {
      ...teeInformationByIdRes?.data[0],
      formattedStartTime: tempStartTime,
      formattedDate: tempFormatDate,
    };

    const initialQuantity = getInitialBookingQuantity(teeTime, slot);

    dispatch(
      clientPortalUpdate({
        selectedTeeTime: selectedTeeTime,
        initialQuantity,
      }),
    );

    if (clientPortalStore?.user) {
      //If not null - User has access to their own locked teetime
      if (teeInformationByIdRes.data[0].lock !== null) {
        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();
        const tempString = String(teeInformationByIdRes.data[0].lock.start_time) + "Z";
        const lockStartDate = new Date(tempString);
        const tempExpiryString = String(teeInformationByIdRes.data[0].lock.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);
        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();
        const lockoutDuration = lockoutExpiryTime - currentTime;

        dispatch(
          clientPortalUpdate({
            selectedTeeTimeLock: {
              lockoutDuration: lockoutDuration,
              lockStartTime: lockStartTime,
              expireTime: teeInformationByIdRes.data[0].lock.expire_time,
            },
          }),
        );

        const holeSelectionAddress = `/portal/${clientShortName}/booking/id?=${tempId}&date=${tempDate}`;
        sendToNextPage(holeSelectionAddress);
      } else {
        const bookingLockRes = await LockTeeTime({ tee_time_id: teeInformationByIdRes.data[0].id }, true);

        if (bookingLockRes.status !== StatusCode.OK) {
          dispatch(showError(String(bookingLockRes.data.message)));
          return;
        }

        if (bookingLockRes.data.message === "Tee time already locked by user") {
          dispatch(showError(t("Booking is locked by another user, please try again in a few minutes.")));
          console.log("Booking is locked by another user");
          return;
        }

        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();
        const tempString = String(bookingLockRes.data.start_time) + "Z";
        const lockStartDate = new Date(tempString);
        const tempExpiryString = String(bookingLockRes.data.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);
        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();
        const lockoutDuration = lockoutExpiryTime - currentTime;

        dispatch(
          clientPortalUpdate({
            selectedTeeTimeLock: {
              lockoutDuration: lockoutDuration,
              lockStartTime: lockStartTime,
              expireTime: bookingLockRes.data.expire_time,
            },
          }),
        );

        const bookingAddress = `/portal/${clientShortName}/booking/id?=${tempId}&date=${tempDate}`;
        sendToNextPage(bookingAddress);
      }
    } else {
      const loginAddress = `/portal/${clientShortName}/login?id=${tempId}&date=${tempDate}&facility=${clientPortalStore?.selectedFacility?.short_name}`;
      sendToNextPage(loginAddress);
    }
  }

  function handleFacilityChange(id: number) {
    const foundFacility = clientPortalStore?.client?.facilities?.find(facility => facility?.id === id);

    if (foundFacility) {
      ReactDOM.unstable_batchedUpdates(() => {
        dispatch(clientPortalUpdate({ selectedFacility: foundFacility, teeSheetSettings: null }));
        setTeeTimeTab("list");
      });

      cancelRequest();

      void loadTeeTimes(
        foundFacility,
        true,
        filterState.selectedDate,
        !!clientPortalStore?.user,
        cancelToken(),
        true,
        leagueState?.selectedLeague?.id,
        teeTimeTab,
        filterState?.selectedCourseId,
      );
    }
  }

  const selectedFacilityElement = (
    <div className="client-portal-home-course-information-section">
      {clientPortalStore?.client?.facilities?.length > 1 && clientPortalStore?.selectedFacility?.logo_source && (
        <div>
          <img className="client-portal-home-course-logo" src={clientPortalStore?.selectedFacility?.logo_source} />
        </div>
      )}
      <div>
        <div className="client-portal-home-coursename">{clientPortalStore?.selectedFacility?.long_name}</div>
        {clientPortalStore?.selectedFacility && (
          <div className="client-portal-home-course-secondary-information">
            {`${clientPortalStore?.selectedFacility?.address_line_1}, ${clientPortalStore?.selectedFacility?.city}, ${clientPortalStore?.selectedFacility?.province_name}, ${clientPortalStore?.selectedFacility?.postal}`}
          </div>
        )}
        {clientPortalStore?.selectedFacility?.address_line_2 && (
          <div className="client-portal-home-course-secondary-information">
            {clientPortalStore?.selectedFacility?.address_line_2}
          </div>
        )}
      </div>
    </div>
  );

  async function handleAddToFavourites() {
    const favRes = await PostFacilityFavourite({ facility_id: clientPortalStore?.selectedFacility?.id }, true);

    if (favRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding course to favourites")));
      return;
    }

    void updateFavouriteStatus();
    dispatch(showSuccess(t("Successfully added course to favourites")));
  }

  async function handleRemoveFromFavourites() {
    const favRes = await DeleteFacilityFavourite({ facility_id: clientPortalStore?.selectedFacility?.id }, true);

    if (favRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing course from favourites")));
      return;
    }

    void updateFavouriteStatus();
    dispatch(showSuccess(t("Successfully removed course from favourites")));
  }

  function updateFavouriteStatus() {
    if (!clientPortalStore?.client) {
      return;
    }

    const updatedFacilities = [...clientPortalStore.client.facilities];
    const foundIndex = updatedFacilities?.findIndex(
      facility => facility?.id === clientPortalStore?.selectedFacility?.id,
    );

    if (foundIndex !== -1) {
      updatedFacilities[foundIndex] = {
        ...updatedFacilities[foundIndex],
        favourite: !updatedFacilities[foundIndex]?.favourite,
      };

      const sortedFacilities = sortFacilities([...updatedFacilities]);
      const client = { ...clientPortalStore?.client, facilities: sortedFacilities };

      dispatch(clientPortalUpdate({ client, selectedFacility: updatedFacilities[foundIndex] }));
    } else {
      dispatch(showError(t("Error updating course favourites")));
    }
  }

  function handleFacilitySearch(query: string) {
    setFilterState(prevState => ({ ...prevState, facilitySearchQuery: query }));
  }

  async function logoutUser() {
    const logoutRes = await UserLogout(true);
    if (logoutRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error logging out")));
    }
    window.location.reload();
  }

  /**
   *
   * @param {Partial<IFacility>} facility The selected facility object
   * @param {boolean} resetValues Boolean that determines if the facility tee sheet settings and leagues are reloaded
   * @param {Date} selectedDate The selected date object
   * @param {boolean} userActive Boolean that determiens if the user is logged in or not
   * @param {CancelToken} cancelToken Axios cancel token
   * @param {boolean} reloadNotifications Boolean that determines if the tee sheet notifications are reloaded
   * @param {number} leagueId The selected league id
   * @param {string} teeTimeTabId The id that determines which tee time view is selected (ex: "list" | "tee_sheet")
   * @param {string} courseId The selected course id. -1 represents all courses
   * @returns
   */
  async function loadPromotionDates(
    facilityId: number,
    startDate: string,
    endDate: string,
    userActive: boolean,
    token: CancelToken,
  ) {
    const res = userActive
      ? await GetTeeTimePromotionDates(
          { facility_id: facilityId, start_date: startDate, end_date: endDate },
          false,
          token,
        )
      : await GetGuestTeeTimePromotionDates(
          { facility_id: facilityId, start_date: startDate, end_date: endDate },
          false,
          token,
        );

    if (token.reason) {
      return;
    }

    if (res?.status !== StatusCode.OK || !Array.isArray(res?.data)) {
      setPromotionDates([]);
      return;
    }

    setPromotionDates(res.data);
  }

  async function loadTeeTimes(
    facility: Partial<IFacility>,
    resetValues: boolean,
    selectedDate: Date,
    userActive: boolean,
    cancelToken: CancelToken,
    reloadNotifications: boolean,
    leagueId: number,
    teeTimeTabId: TWebTeeTimes,
    courseId: number,
  ) {
    let teeTimesTab = teeTimeTabId;

    ReactDOM.unstable_batchedUpdates(() => {
      if (resetValues) {
        setLeagueState(prevState => ({
          ...prevState,
          leagues: [{ id: -1, name: t("All Tee Times") }],
          selectedLeagueId: -1,
        }));
      }

      setFilterState(prevState => ({
        ...prevState,
        teeTimeRes: null,
        notifications: reloadNotifications || resetValues ? [] : prevState?.notifications,
        selectedCourseId: resetValues ? -1 : prevState?.selectedCourseId,
      }));
    });

    let teeTimeRes: IAPIResponse<Array<ITeeTime>>;
    let teeSheetSettings = clientPortalStore?.teeSheetSettings;

    // Load leagues in the background
    if (resetValues && userActive) {
      void loadLeagues(facility?.id, cancelToken);
    }

    if (reloadNotifications) {
      //Load tee sheet notifications in the background
      void loadNotifications(selectedDate, userActive, facility?.id, cancelToken);
    }

    //User is logged in, use Customer endpoints
    if (userActive) {
      if (resetValues || !teeSheetSettings) {
        const teeSheetSettingsRes = await GetCustomerTeeSheetSettings(
          { facility_id: facility?.id },
          false,
          cancelToken,
        );

        if (teeSheetSettingsRes.status !== StatusCode.OK) {
          if (cancelToken && cancelToken?.reason) {
            return;
          }
          dispatch(showError(t("Error getting tee sheet settings")));
        }

        teeSheetSettings = teeSheetSettingsRes.data;
        teeTimesTab = teeSheetSettings?.web_tee_times_display;

        ReactDOM.unstable_batchedUpdates(() => {
          setTeeTimeTab(teeSheetSettings?.web_tee_times_display ?? "list");
          dispatch(
            clientPortalUpdate({
              teeSheetSettings: teeSheetSettings,
            }),
          );
        });
      }

      teeTimeRes = await GetTeeTimesCustomer(
        {
          facility_id: facility?.id,
          date: selectedDate,
          extended: true,
          turn_tee_time: true,
          league_id: leagueId <= 0 || resetValues ? undefined : leagueId,
          tee_sheet: teeTimesTab === "tee_sheet",
          course_id: courseId <= 0 || resetValues ? undefined : courseId,
        },
        false,
        cancelToken,
      );

      if (teeTimeRes?.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
      }
    } else {
      //User is not logged in, use Guest endpoints
      if (resetValues || !teeSheetSettings) {
        //Guest API call
        const teeSheetSettingsRes = await GetGuestTeeSheetSettings({ facility_id: facility?.id }, false, cancelToken);

        if (teeSheetSettingsRes.status !== StatusCode.OK) {
          if (cancelToken && cancelToken?.reason) {
            return;
          }
          dispatch(showError(t("Error getting tee sheet settings")));
        }

        teeSheetSettings = teeSheetSettingsRes.data;

        ReactDOM.unstable_batchedUpdates(() => {
          //Guest can only view tee times list view
          setTeeTimeTab("list");
          dispatch(
            clientPortalUpdate({
              teeSheetSettings: teeSheetSettings,
            }),
          );
        });
      }

      teeTimeRes = await GetTeeTimesGuest(
        {
          facility_id: facility?.id,
          date: selectedDate,
          extended: true,
          turn_tee_time: true,
          course_id: courseId <= 0 || resetValues ? undefined : courseId,
        },
        false,
        cancelToken,
      );

      if (teeTimeRes?.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
      }
    }

    // Attempt to load tee times with access code that was previously successfully entered
    if (
      ((teeTimeRes?.data && teeTimeRes?.data?.length === 0) || teeTimeRes?.status !== StatusCode.OK) &&
      clientPortalStore?.accessCode &&
      (leagueId <= 0 || resetValues)
    ) {
      void getTeeTimesAccessCode(clientPortalStore?.accessCode, teeTimesTab === "tee_sheet", selectedDate);
      return;
    }

    ReactDOM.unstable_batchedUpdates(() => {
      dispatch(
        clientPortalUpdate({
          usingAccessCode: false,
        }),
      );

      setFilterState(prevState => ({
        ...prevState,
        teeTimeRes,
        enable_cart_bookings: !!teeSheetSettings?.enable_cart_bookings,
      }));
    });
  }

  async function loadNotifications(date: Date, userActive: boolean, facilityId: number, cancelToken: CancelToken) {
    let notificationResponse: IAPIResponse<Array<INotification>>;
    const currentDateString = date.toLocaleDateString("en-CA");

    if (userActive) {
      // Members || Private Api Call
      notificationResponse = await GetCustomerNotifications(
        {
          date: currentDateString,
          facility_id: facilityId,
        },
        false,
        cancelToken,
      );
    } else {
      //Guest || Pubic Api call
      notificationResponse = await GetGuestNotifications(
        {
          date: currentDateString,
          facility_id: facilityId,
        },
        false,
        cancelToken,
      );
    }

    if (notificationResponse?.status === StatusCode.OK && notificationResponse?.data?.length > 0) {
      setFilterState(prevState => ({ ...prevState, notifications: notificationResponse.data }));
    } else {
      setFilterState(prevState => ({ ...prevState, notifications: [] }));
    }
  }

  async function loadLeagues(facilityId: number, cancelToken: CancelToken) {
    const leagueRes = await GetCustomerLeague(
      { facility_id: facilityId, year: filterState?.selectedDate.getFullYear(), user_id: clientPortalStore?.user?.id },
      false,
      cancelToken,
    );

    if (leagueRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error getting leagues")));
    } else if (leagueRes.status === StatusCode.OK) {
      const updatedLeagues = [{ id: -1, name: t("All Tee Times") }, ...leagueRes.data];

      setLeagueState({
        leagues: updatedLeagues,
        selectedLeague: { id: -1, name: t("All Tee Times") },
      });
    }
  }

  function handleSelectLeague(league: Partial<ILeague>) {
    const teeTimeDisplay = league?.web_tee_times_display ?? clientPortalStore?.teeSheetSettings?.web_tee_times_display;

    ReactDOM.unstable_batchedUpdates(() => {
      setLeagueState(prevState => ({ ...prevState, selectedLeague: league }));
      setTeeTimeTab(teeTimeDisplay);
    });

    cancelRequest();

    if (clientPortalStore?.selectedFacility) {
      void loadTeeTimes(
        clientPortalStore?.selectedFacility,
        false,
        filterState.selectedDate,
        !!clientPortalStore?.user,
        cancelToken(),
        false,
        league?.id,
        teeTimeDisplay,
        filterState?.selectedCourseId,
      );
    }
  }

  function handleSelectTeeTimeView(tabId: TWebTeeTimes) {
    setTeeTimeTab(tabId);
    cancelRequest();

    if (clientPortalStore?.selectedFacility) {
      void loadTeeTimes(
        clientPortalStore?.selectedFacility,
        false,
        filterState?.selectedDate,
        !!clientPortalStore?.user,
        cancelToken(),
        false,
        leagueState?.selectedLeague?.id,
        tabId,
        filterState?.selectedCourseId,
      );
    }
  }

  function handleSelectCourse(courseId: number) {
    setFilterState(prev => ({ ...prev, selectedCourseId: courseId }));
    cancelRequest();

    void loadTeeTimes(
      clientPortalStore?.selectedFacility,
      false,
      filterState.selectedDate,
      !!clientPortalStore?.user,
      cancelToken(),
      false,
      leagueState?.selectedLeague?.id,
      teeTimeTab,
      courseId,
    );
  }

  async function getTeeTimesAccessCode(accessCode: string, teesheet: boolean, date?: Date) {
    if (!accessCode) {
      return;
    }

    cancelRequest();

    // loader cards
    setFilterState(prevState => ({
      ...prevState,
      teeTimeRes: null,
    }));

    const selectedDate = date ?? filterState?.selectedDate;

    const res = await GetTeeTimesAccessCode(
      {
        facility_id: clientPortalStore?.selectedFacility?.id,
        date: formatDate(selectedDate),
        access_code: accessCode,
        tee_sheet: teesheet,
      },
      false,
      cancelToken(),
    );

    if (res?.status !== StatusCode.OK) {
      if (cancelToken()?.reason) {
        return;
      }

      setFilterState(prevState => ({
        ...prevState,
        teeTimeRes: { status: 400, message: "", data: [] },
      }));

      dispatch(
        showError(typeof res?.data === "string" ? res?.data : res?.message ? res?.message : t("Incorrect access code")),
      );
      return;
    }

    //Save access code in redux
    if (res?.data && res?.data?.length > 0 && accessCode !== clientPortalStore?.accessCode) {
      dispatch(
        clientPortalUpdate({
          accessCode: accessCode,
        }),
      );
    }

    ReactDOM.unstable_batchedUpdates(() => {
      dispatch(
        clientPortalUpdate({
          usingAccessCode: true,
        }),
      );

      setFilterState(prevState => ({
        ...prevState,
        teeTimeRes: res,
      }));
    });
  }

  return (
    <div className="client-portal-home-page-container">
      <ClientPortalNavigation showNavOptions />

      <div className="client-portal-home-wide-section-container">
        <div className="client-portal-home-wide-section">
          <div className="client-portal-home-facility-container">
            {!clientPortalStore?.client?.facilities ? (
              <LoaderCard image textRows={2} customCSSProperties={{ margin: "30px 0" }} />
            ) : clientPortalStore?.client?.facilities?.length > 1 ? (
              <>
                <div className="client-portal-home-facility-dropdown">
                  <CustomDropdown
                    parentElement={selectedFacilityElement}
                    onChange={handleFacilityChange}
                    defaultValue={clientPortalStore?.selectedFacility?.id}
                    onSearch={handleFacilitySearch}
                    searchPlacerHolder={t("Search courses...")}
                  >
                    {facilities ? (
                      facilities?.map((facility, index) => {
                        return (
                          <CustomDropdownOption value={facility.id} key={index}>
                            <div className="client-portal-facility-option-container">
                              <img
                                className="client-portal-facility-option-facility-logo"
                                src={facility?.logo_source}
                              />
                              <p className="client-portal-facility-option-facilityname">{facility?.long_name}</p>
                              {windowSize.width > MOBILE_WIDTH ? (
                                <p className="client-portal-facility-option-facilityaddress">{facility?.city}</p>
                              ) : null}
                              {facility?.favourite && (
                                <FontAwesomeIcon
                                  size="1x"
                                  fixedWidth
                                  className="client-portal-facility-option-favourite"
                                  icon={faStar}
                                />
                              )}
                            </div>
                          </CustomDropdownOption>
                        );
                      })
                    ) : (
                      <div className="facility-spinner">
                        <Spin />
                      </div>
                    )}
                  </CustomDropdown>
                </div>

                {clientPortalStore?.user && clientPortalStore?.client?.facilities?.length > 1 && (
                  <OverlayTrigger
                    placement="auto"
                    overlay={
                      <Tooltip id="courseFavouritesToolTip">
                        {clientPortalStore?.selectedFacility?.favourite
                          ? `${t("Remove from favourites")}`
                          : `${t("Add to favourites")}`}
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon
                      onClick={
                        clientPortalStore?.selectedFacility?.favourite
                          ? () => handleRemoveFromFavourites()
                          : () => handleAddToFavourites()
                      }
                      onMouseEnter={() => setAnimateFavourites(true)}
                      onMouseLeave={() => setAnimateFavourites(false)}
                      beatFade={animateFavourites}
                      className={classNames("client-portal-favourites-icon", {
                        "client-portal-favourites-icon-favourited": clientPortalStore?.selectedFacility?.favourite,
                      })}
                      icon={faStar}
                    />
                  </OverlayTrigger>
                )}
              </>
            ) : (
              selectedFacilityElement
            )}
          </div>
        </div>
      </div>

      {clientPortalStore?.user && leagueState?.leagues?.length > 1 && (
        <div className="client-portal-home-tabs-parent-container">
          <div className="client-portal-home-tabs-narrow-section">
            <div className="client-portal-home-tabs-container">
              {leagueState?.leagues?.map((league, leagueIndex) => {
                return (
                  <div
                    key={leagueIndex}
                    className={classNames("events-tab", {
                      "events-tab-selected": league?.id === leagueState?.selectedLeague?.id,
                    })}
                    onClick={() => handleSelectLeague(league)}
                  >
                    {league?.name}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="client-portal-home-narrow-section-container">
        <div className="client-portal-home-narrow-section">
          <div className="client-portal-home-date-selection">
            <div className="client-portal-home-date-selector">
              <PortalDatePickerInput
                windowWidth={windowSize.width}
                fullLayout
                startingDate={filterState.selectedDate}
                setStartingDate={handleSelectedDateChange}
                disablePastDays
              />

              {windowSize.width <= MOBILE_WIDTH && teeTimeTab === "list" && (
                <ClientPortalTeetimeFilters
                  filters={{
                    selectedCart: filterState.selectedCart,
                    selectedHoles: filterState.selectedHoles,
                    selectedPlayers: filterState.selectedPlayers,
                    selectedPromotion: filterState.selectedPromotion,
                    teeTimeRes: filterState.teeTimeRes,
                  }}
                  updateFilters={filters => updateFilterState({ ...filters })}
                  showCartFilters={filterState.enable_cart_bookings}
                  showPromotionFilters={(filterState.teeTimeRes?.data ?? []).some(
                    teeTime => Boolean(teeTime?.promotion_type) && teeTime?.promotion_value != null,
                  )}
                />
              )}
            </div>

            <div className="client-portal-home-date-bar">
              <DateBar
                numberOfDays={9}
                selectedDate={filterState.selectedDate}
                onClick={clientPortalStore?.selectedFacility ? date => handleSelectedDateChange(date) : () => {}}
                showNavigation={getPortalDateBarShowNavigation(windowSize.width, true)}
                highlightedDates={promotionDates}
                onVisibleRangeChange={(startDate, endDate) => {
                  const start = formatDate(startDate);
                  const end = formatDate(endDate);
                  setVisibleDateRange(prev =>
                    prev?.start === start && prev?.end === end ? prev : { start, end },
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="client-portal-home-wide-section-container">
        <div className="client-portal-home-wide-section client-portal-home-tee-time-section">
          {!!clientPortalStore?.user &&
            ((leagueState?.selectedLeague?.id !== -1 &&
              leagueState?.selectedLeague?.web_tee_times_display !== "list") ||
              (leagueState?.selectedLeague?.id === -1 &&
                clientPortalStore?.teeSheetSettings &&
                clientPortalStore?.teeSheetSettings?.web_tee_times_display !== "list")) && (
              <div>
                <div className="client-portal-tabs-container">
                  <div className="mt-2">
                    <Toggle
                      labelRight="Show restricted bookings"
                      checked={showRestrictedBookings}
                      onChange={() => setShowRestrictedBookings(prev => !prev)}
                    />
                  </div>

                  {teeTimeTabs(t)?.map((tab, tabIndex) => {
                    return (
                      <div
                        key={tabIndex}
                        className={classNames("client-portal-tee-times-tab", {
                          "client-portal-tee-times-tab-selected": tab.id === teeTimeTab,
                        })}
                        onClick={() => handleSelectTeeTimeView(tab.id)}
                      >
                        {tab?.title}
                        <FontAwesomeIcon icon={["far", tab.icon]} size="1x" style={{ marginLeft: "1rem" }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          <div className="client-portal-home-main-sections">
            {!!clientPortalStore?.user && teeTimeTab === "tee_sheet" ? (
              <ClientPortalTeeSheet
                checkTeeTime={(date, teeTime, slot) => checkTeeTime(date, teeTime, slot)}
                teeTimeRes={filterState.teeTimeRes}
                notifications={filterState.notifications}
                showRestrictedBookings={showRestrictedBookings}
                getTeeTimesAccessCode={getTeeTimesAccessCode}
                leagueSelected={leagueState?.selectedLeague?.id !== -1}
                filterState={filterState}
                onCourseSelect={handleSelectCourse}
              />
            ) : (
              <ClientPortalTeeTimesList
                checkTeeTime={(date, teeTime) => checkTeeTime(date, teeTime)}
                dayOfWeekNumber={filterDayState}
                filterState={filterState}
                setFilterState={setFilterState}
                teeTimeRes={filterState?.teeTimeRes}
                notifications={filterState.notifications}
                showRestrictedBookings={showRestrictedBookings}
                getTeeTimesAccessCode={getTeeTimesAccessCode}
                leagueSelected={leagueState?.selectedLeague?.id !== -1}
                onCourseSelect={handleSelectCourse}
              />
            )}
          </div>
        </div>
      </div>

      <TeeTimeWaitlist
        visible={
          !!clientPortalStore?.teeSheetSettings?.booking_engine_display_waitlist &&
          !!clientPortalStore?.selectedFacility?.module_waitlist
        }
        courseLogo={clientPortalStore?.selectedFacility?.logo_source}
        courseName={clientPortalStore?.selectedFacility?.full_name}
        loginUrl={`/portal/${clientShortName}/login`}
        courseAddressLine={clientPortalStore?.selectedFacility?.address_line_1}
        facilityId={clientPortalStore?.selectedFacility?.id}
        activeUser={!!clientPortalStore?.user}
      />

      <Popup
        open={filterState.adminUserLoggedIn}
        type="warning"
        title={t("Admin Account Detected")}
        description={t("Looks like you are logged into an admin account. Click logout below to view the tee times")}
        onOk={logoutUser}
        okText={t("Logout")}
      />
    </div>
  );
}

function teeTimeTabs(t: TFunction): Array<{ id: TWebTeeTimes; title: string; icon: IconName }> {
  return [
    {
      id: "list",
      title: t("List"),
      icon: "list",
    },
    {
      id: "tee_sheet",
      title: t("Tee Sheet"),
      icon: "bars-staggered",
    },
  ];
}
